import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {NoAccess} from 'components/pages/NoAccess';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Link} from 'components/ui-kit/Link';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ROUTE} from 'core/routes';
import {factoriesState$, Gate} from 'models/profile/factories/factoriesGrid';
import {isCreateModalOpenChanged} from 'models/profile/factories/factoryOperations';
import {UserPermission} from 'types';
import {
    FactoriesAccessors as Accessors,
    FactoriesListFormattedRow,
    FactoriesListRawRow,
} from 'types/profile';
import {usePermission} from 'utils/custom-hooks';
import {handleNil} from 'utils/handleNil';
import {formatNumber} from 'utils/number';

import {ProfileNavigationTabs} from '../../NavigationTabs';
import {Wrapper} from '../../styled';
import {FactoryCreate} from '../FactoryCreate';

import {CreateButton, HeaderControls} from './styled';

const getConfig = (t: TFunction) => [
    {
        header: t('pages.profile.factories.name'),
        id: Accessors.name,
    },
    {
        header: t('pages.profile.factories.id'),
        id: Accessors.id,
    },
    {
        header: t('pages.profile.factories.address'),
        id: Accessors.address,
    },
    {
        header: t('pages.profile.factories.linesCount'),
        id: Accessors.linesCount,
    },
];

export const Factories = () => {
    useGate(Gate);
    const hasViewPermission = usePermission([UserPermission.FACTORY_CARD_VIEW]);

    const convertData = (data: FactoriesListRawRow[] | undefined): FactoriesListFormattedRow[] => {
        if (!data) return [];

        return data.map((elem) => ({
            [Accessors.name]: handleNil(elem.name, (value: string) =>
                hasViewPermission ? (
                    <Link to={generatePath(`${ROUTE.PROFILE}/factories/:id`, {id: elem.id})}>
                        {value}
                    </Link>
                ) : (
                    <div>{value}</div>
                ),
            ),
            [Accessors.id]: handleNil(elem.id),
            [Accessors.address]: handleNil(elem.address),
            [Accessors.linesCount]: handleNil(elem.factoryLinesTotalCount, (value) =>
                formatNumber(value),
            ),
            [Accessors.manufacturerId]: handleNil(elem.manufacturerId),
            [Accessors.code]: handleNil(elem.code),
        }));
    };

    const {t} = useTranslation();
    const {data, isLoading} = useStore(factoriesState$);
    const columns = useMemo(() => mapTableColumns(getConfig(t)), [t]);

    const mappedData = React.useMemo(() => convertData(data), [data, t]);

    const CreateFactory = () => (
        <PermissionAccess permissions={[UserPermission.FACTORY_CONTROL_CREATE]}>
            <FactoryCreate />
            <CreateButton size="sm" type="button" onClick={() => isCreateModalOpenChanged(true)}>
                {t('pages.profile.createFactory.add')} +
            </CreateButton>
        </PermissionAccess>
    );

    const headerControls = (
        <HeaderControls>
            <CreateFactory />
        </HeaderControls>
    );

    if (!usePermission([UserPermission.FACTORY_GRID_VIEW])) return <NoAccess />;

    return (
        <Page title={t('pages.profile.title')}>
            <Wrapper>
                <ProfileNavigationTabs />
                <ContentContainer noPaddings>
                    <Table
                        columns={columns}
                        data={mappedData}
                        totalRowsCount={data.length}
                        isLoading={isLoading}
                        headerControls={headerControls}
                        userConfig={{
                            gridName: 'factoriesGrid',
                            saveConfig: saveGridConfig,
                            getConfig: fetchGridConfig,
                        }}
                    />
                </ContentContainer>
            </Wrapper>
        </Page>
    );
};
